/* eslint-disable no-case-declarations */
/* eslint-disable @typescript-eslint/no-explicit-any */
// <copyright file="J2735DataMap.tsx">
// © 2025 Florida Department of Transportation
//
// The copyright to the computer program(s) and source code herein is
// the property of the Florida Department of Transportation.
// The program(s) and source code may be used and/or copied only with the
// written permission of the Florida Department of Transportation or
// in accordance with the terms and conditions stipulated in the
// agreement/contract under which the program(s) and source code have
// been supplied.
// </copyright>

/* eslint-disable autofix/no-unused-vars */
import { lineString, polygon } from "@turf/helpers";
import { lineOffset } from "@turf/line-offset";
import axios from "axios";
import L, { latLng, divIcon, LatLng } from "leaflet";
import "leaflet/dist/leaflet.css";
import iconRetina from "leaflet/dist/images/marker-icon-2x.png";
import iconMarker from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";
import { Dialog } from 'primereact/dialog';
import { Toast } from "primereact/toast";
import React, { useState as _useState, useContext, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, ScaleControl, LayersControl, LayerGroup, Polygon, Polyline, Circle, CircleMarker, useMapEvents } from 'react-leaflet';
import 'leaflet-arrowheads'; // Import leaflet-arrowheads plugin
import { type LaneData, LaneTypeEnum, type IntersectionData, type SignalState } from "src/components/CvdfVisualizer/CombinedDataInterface";
import styles from "src/components/CvdfVisualizer/CvdfVisualizer.module.css"
import { ITISCodes } from "src/components/CvdfVisualizer/ITISCodes";
import { MovementPhaseState } from "src/components/CvdfVisualizer/SPaTDataInterface";
import { type TimData, type Location} from "src/components/CvdfVisualizer/TimDataInterface";
import ConfigContext from "src/context/ConfigContext";
import { displayLoadingError, getCookieJsonField } from "src/utils/SharedUtils";
import { v4 as uuidv4 } from "uuid";
import type { AxiosRequestConfig } from "axios";
import type { default as LEAF, LatLngExpression, LatLngTuple, LatLngBounds, LatLngLiteral} from "leaflet";
import type { FC } from "react";
import type { Lane, LaneType, Maneuver, MapData, MapNode } from "src/components/CvdfVisualizer/MapDataInterface";
import type SPaTData from "src/components/CvdfVisualizer/SPaTDataInterface";
import 'leaflet.awesome-markers/dist/leaflet.awesome-markers.css';
import 'leaflet.awesome-markers';
import type { IConfig } from "src/utils/ConfigLoader";


L.Icon.Default.mergeOptions({
  iconRetinaUrl: iconRetina,
  iconUrl: iconMarker,
  shadowUrl: iconShadow,
});

// Minimum zoom level to render MAP and SPaT
const MIN_ZOOM_LEVEL = 15;
const INITIAL_ZOOM_LEVEL = 9;
const LANE_WEIGHT= 4;
const MINUTE_MS = 60000;
const MAP_REFRESH_MINUTES = 60;
const TIM_REFRESH_MINUTES = 1;

const mapIcon = L.AwesomeMarkers.icon({
      icon: ` ${styles.mui_traffic}`,
      markerColor: 'cadetblue',
      prefix: 'pi'
    });

const timIcon = L.AwesomeMarkers.icon({
      icon: ` ${styles.mui_car_alert}`,
      markerColor: 'blue',
      prefix: 'pi'
    });

// Info/error message popup
const toast = React.createRef<Toast>();

function prepareUrl(baseUrl: string, apiRoute: string): string {
    const fetchRoute = apiRoute.startsWith("/") ? apiRoute : `/${apiRoute}`;
    const baseUri = baseUrl.endsWith("/")
        ? baseUrl.substring(0, baseUrl.length - 1)
        : baseUrl;
    console.debug(`${baseUri}${fetchRoute}`)
    return `${baseUri}${fetchRoute}`;
}

function prepareRequest(apiRoute: string) {
    // Initialize url, headers, and query params
    const authToken = getCookieJsonField(
            "DepSessionIdentity",
            "token"
        );
    const headers = {
        'Content-Type': 'application/json',
        'accept' :'application/json',
        'Authorization': `Bearer ${authToken}`,
    };
    const config: AxiosRequestConfig<any> = {
        headers: headers,
    };

    return config;
}

interface CvdfVisualizerProps {
    useState?: typeof _useState; // optional hook override (for tests)
    cvdfApiBaseUrl: string;
    cvdfRealTimeBaseUrl: string;
}

const CvdfVisualizer: FC<CvdfVisualizerProps> = (props: CvdfVisualizerProps) => {
    const configData = useContext(ConfigContext) as IConfig;
    const mapRef = useRef<LEAF.Map>(null);
    const [, setTIMLayer] = _useState<LEAF.LayerGroup | null>(null);
    const [, setSPaTLayer] = _useState<LEAF.LayerGroup | null>(null);
    const [, setMAPLayer] = _useState<LEAF.LayerGroup | null>(null);
    const [zoomLevel, setZoomLevel] = _useState<number>(mapRef.current?.getZoom() || INITIAL_ZOOM_LEVEL);

    const [dialogVisible, setDialogVisible] = _useState(false);
    const [dialogHeader, setDialogHeader] = _useState("");
    const [dialogContent, setDialogContent] = _useState<JSX.Element | null>(null);
    const [popupInfo, setPopupInfo] = _useState<{ position: LatLngLiteral, content: string } | null>(null);

    const [timData, setTIMData] = _useState<TimData[]>([]);
    const [, setMapData] = _useState<MapData[]>([]);
    const [combinedData, setCombinedData] = _useState<Map<number, IntersectionData>>(new Map());
    const [intersectionIds, setIntersectionIds] = _useState<number[]>([]);

    // WebSocket retry configuration
    let retryCount = 0;
    const maxRetries = 5;
    const retryDelay = 1000; // Initial delay in milliseconds
    const ws = React.useRef<WebSocket | null>(null);

    const fetchTIMs = async () => {
        try {
            const route = prepareUrl(props.cvdfApiBaseUrl, `/traveler-information-messages`);
            const config = prepareRequest(route);
            const data = { latestOnly: true };
            const response = await axios.post(route, data, config)
            setTIMData(response.data);
            return
        } catch (error) {
            console.error("Failed to fetch data", error);
            return [];
        }
    };

    const checkLaneType = (lane: LaneType): LaneTypeEnum | null => {
        if (lane.laneAttributesVehicle) return LaneTypeEnum.Vehicle;
        if (lane.laneAttributesCrosswalk) return LaneTypeEnum.Crosswalk;
        if (lane.laneAttributesBike) return LaneTypeEnum.Bike;
        if (lane.laneAttributesSidewalk) return LaneTypeEnum.Sidewalk;
        if (lane.laneAttributesBarrier) return LaneTypeEnum.Barrier;
        if (lane.laneAttributesStriping) return LaneTypeEnum.Striping;
        if (lane.laneAttributesTrackedVehicle) return LaneTypeEnum.TrackedVehicle;
        if (lane.laneAttributesParking) return LaneTypeEnum.Parking;

        return null;
        };

    const fetchPageData = async (route: string, config: AxiosRequestConfig, pagenum: number) => {
        const response = await axios.post(route, { "page": pagenum, "latestOnly": true }, config);
        return response.data;
    };

    const processRecord = (record: MapData) => {
        const intersection = record.maprecord.mapData.intersection;
        if (!intersection?.laneSet) return null;

        const intersectionId = intersection.id.id;
        return {
            intersectionId,
            data: {
                intersectionPoint: [intersection.refPoint.latitude, intersection.refPoint.longitude],
                lanes: intersection.laneSet.map(processLane),
                signalStates: new Map(), // Placeholder for signal states
                mapJ2735Uper: record.maprecord.mapJ2735Uper,
                region: intersection.id.region,
                mapIngested: record.metadata.time.ingested,
            }
        };
    };

    const processLane = (lane: Lane) => {
        const laneEndPoint = lane.nodeList.nodes[0];
        const signalGroups = lane.connectsTo ? Array.from(new Set(lane.connectsTo.map((c) => c.signalGroup))) : [];
        return {
            laneId: lane.laneID,
            egressPath: lane.laneAttributes.directionalUse.egressPath,
            endPoint: [laneEndPoint.delta.nodeLatLon.latitude, laneEndPoint.delta.nodeLatLon.longitude],
            signalGroups: signalGroups,
            nodeList: lane.nodeList,
            maneuvers: lane.maneuvers,
            laneType: checkLaneType(lane.laneAttributes.laneType)
        };
    };

    const fetchMapRecords = async () => {
        try {
            const route = prepareUrl(props.cvdfApiBaseUrl, "/intersection-maps");
            const config = prepareRequest(route);
            let pagenum = 0;
            let results:  MapData[]= [];
            let contin = false;
            const newCombinedData = new Map();

            do {
                const pageData = await fetchPageData(route, config, pagenum);
                results = [...results, ...pageData];
                contin = pageData.length >= 100;
                pagenum++;
            setMapData(results);
            results.forEach((record) => {
                try {
                    const processedRecord = processRecord(record);
                    if (processedRecord) {
                        newCombinedData.set(processedRecord.intersectionId, processedRecord.data);
                    }
                } catch (error) {
                    console.error(error);
                }
            });

            setCombinedData(newCombinedData);

            } while (contin);

            updateSPaTData();
        } catch (error) {
            console.error("Failed to fetch map records", error);
        }
    };

    const loadData = () => {
        fetchTIMs();
        fetchMapRecords();
    }

    useEffect(() => {
        loadData();

        // Unsubscribe and close the websocket
        if (ws.current){
            intersectionIdsRef.current = [];
            ws.current.close();
        }
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            fetchTIMs();
        }, MINUTE_MS * TIM_REFRESH_MINUTES);

        return () => clearInterval(interval);
    }, [])

    useEffect(() => {
        const interval = setInterval(() => {
            fetchMapRecords();
        }, MINUTE_MS * MAP_REFRESH_MINUTES);

        return () => clearInterval(interval);
    }, [])

    // Use useEffect to establish WebSocket connection when combinedData is updated
    useEffect(() => {
        if((intersectionIds.length == 0 || combinedData.size == 0) && ws.current?.readyState == WebSocket.OPEN){
            console.debug("No Intersections requested. Closing websocket.")
            ws.current?.close();
        }
        else if (combinedData.size > 0 && intersectionIds.length > 0 && ws.current?.readyState != WebSocket.OPEN) {
            manageWebSocket();
        }
        else if(combinedData.size > 0 && intersectionIds.length > 0 && ws.current?.readyState == WebSocket.OPEN){
            console.debug(`Requesting SPaT data for intersections ${intersectionIds}`);
            const bearerApiKey = `${getCookieJsonField(
                    "DepSessionIdentity",
                    "token"
                )}`
            ws.current?.send(JSON.stringify({
                    messageType: "authorize",
                    authToken: bearerApiKey,
                    intersections: intersectionIds,
                    format: "json"
            }));
        }
    }, [intersectionIds]);

    const bufferedData = useRef(new Map());
    const intersectionIdsRef = useRef<number[]>(intersectionIds);

    useEffect(() => {
        intersectionIdsRef.current = intersectionIds;
    }, [intersectionIds]);

    const updateSPaTData = () => {
        setCombinedData(prevCombinedData => {
            const updatedCombinedData = new Map(prevCombinedData);
            bufferedData.current.forEach((intersectionData, intersectionId) => {
                // Clear old cached SPaT Data no longer being requested
                if(!intersectionIds.includes(intersectionId)){
                    bufferedData.current.delete(intersectionId)
                    const oldIntersectionData = updatedCombinedData.get(intersectionId);
                    oldIntersectionData?.signalStates.clear();
                }
                updatedCombinedData.set(intersectionId, intersectionData);
            })
          return updatedCombinedData;
        });
    };

    useEffect(() => {
        const interval = setInterval(updateSPaTData, 1000); // 1 Hz interval
        return () => clearInterval(interval);
    }, [intersectionIds]);

    const processSpatRecord = (spatRecord: SPaTData, combinedData: Map<number, IntersectionData>, bufferedData: React.MutableRefObject<Map<number, IntersectionData>>) => {
        try{
            const intersectionId = spatRecord.dataJson.spatrecord.intersection.id.id;
            if (combinedData.has(intersectionId)) {
                const intersectionData = combinedData.get(intersectionId);
                if (intersectionData) {
                    const newSpatTimestamp = new Date(spatRecord.dataJson.metadata.time.ingested);
                    const existingSpatTimestamp = intersectionData.spatIngested ? new Date(intersectionData.spatIngested) : null;

                    // Only update if the new SPaT record is newer than the existing one
                    if (!existingSpatTimestamp || newSpatTimestamp > existingSpatTimestamp) {
                        // Clear existing signal states
                        intersectionData.signalStates.clear();

                        if( intersectionIds.includes(intersectionId) ){
                            // Update signal states with new data
                            spatRecord.dataJson.spatrecord.intersection.states.forEach((state: any) => {
                                intersectionData.signalStates.set(state.signalGroup, state);
                            });
                        }

                        intersectionData.spatJ2735Uper = spatRecord.dataJson.spatrecord.spatJ2735Uper;
                        intersectionData.spatIngested = spatRecord.dataJson.metadata.time.ingested;

                        // Update buffered data
                        bufferedData.current.set(intersectionId, intersectionData);
                    }
                }
            }
        } catch (e) {
            console.error(`Failed to process SPaT record for Intersection: ${spatRecord?.intersectionId}`, e)
        }
    };

    const manageWebSocket = () => {
        const connect = () => {
            if (!ws.current || ws.current.readyState == WebSocket.CLOSED){
                ws.current = new WebSocket(`${props.cvdfRealTimeBaseUrl}/api/cvdf/intersection/spat`);
            }

            const bearerApiKey = `${getCookieJsonField(
                    "DepSessionIdentity",
                    "token"
                )}`
            ws.current.onopen = () => {
                console.debug('WebSocket connection opened');
                console.debug(`Requesting SPaT data for intersections ${intersectionIdsRef.current}`);
                if (ws.current?.readyState === WebSocket.OPEN) {
                    ws.current?.send(JSON.stringify({
                        messageType: "authorize",
                        authToken: bearerApiKey,
                        intersections: intersectionIdsRef.current,
                        format: "json"
                    }));
                }
            };

            ws.current.onmessage = (event: MessageEvent) => {
                const message = JSON.parse(event.data);
                if (message.messageType === 'Data') {
                    const spatRecord = message as SPaTData;
                    processSpatRecord(spatRecord, combinedData, bufferedData);
                }
                if (message.messageType === 'Unauthorized') {
                    console.debug(retryCount);
                }
                if (message.messageType === 'Authorized') {
                    retryCount = 0; // Reset retry count on successful connection
                    console.debug("Web socket authorized");
                }
            };

            ws.current.onclose = () => {
                console.debug('WebSocket connection closed');
                if (intersectionIdsRef.current.length > 0){
                    retryConnection();
                }
            };

            ws.current.onerror = (error) => {
                console.error('WebSocket error', error);
                retryConnection();
            };
        };

        const retryConnection = () => {
            if (retryCount < maxRetries) {
                retryCount++;
                const delay = retryDelay * Math.pow(2, retryCount);
                console.debug(`Retrying connection in ${delay} ms...`);
                setTimeout(connect, delay);
            } else {
                const detail = 'Max retries reached. Could not establish WebSocket connection.';
                const summary = 'Could not establish connection to SPaT Data Source'
                console.error(detail);
                displayLoadingError(
                                summary,
                                detail,
                                toast.current,
                                null
                            );
            }
        };
        if (ws.current?.readyState == WebSocket.CONNECTING || ws.current?.readyState == WebSocket.OPEN) return null;

        connect();
    };

    /**
     * Function to format the duration from milliseconds to "X days, Y hours, Z minutes"
     * @param {number} milliseconds
     * @return {string} formatted duration
     */
    const formatDuration = (milliseconds:number) => {
        if( milliseconds == 0 || milliseconds == null){
            return "Infinite";
        }
        const totalMinutes = Math.floor(milliseconds / 1000 / 60);
        const minutes = totalMinutes % 60;
        const totalHours = Math.floor(totalMinutes / 60);
        const hours = totalHours % 24;
        const days = Math.floor(totalHours / 24);

        return `${days} days, ${hours} hours, ${minutes} minutes`;
    };

    /**
     * Function to calculate the end time from the start time and duration in milliseconds
     * @param {string} startTime - ISO 8601 formatted start time
     * @param {number} duration - Duration in milliseconds
     * @return {string} formatted end time as ISO 8601 string
     */
    const calculateEndTime = (startTime:string, duration:number) => {
        if( duration == 0 || duration == null){
            return "Infinite";
        }
        const startDate = new Date(startTime);
        const endDate = new Date(startDate.getTime() + duration);
        return endDate.toISOString();
    };

    const createBoundingPolygon = (geometryLocation:any, offsetDistance:number) => {
        // Convert points to coordinates
        const coordinates = geometryLocation.points.map((point: any) => [point.absolute.longitude, point.absolute.latitude]);

        // Create the original line
        const line = lineString(coordinates);

        // Offset the line to the left and right
        const offsetLeft = lineOffset(line, offsetDistance);
        const offsetRight = lineOffset(line, -offsetDistance);

        // Get coordinates from the offset lines
        const coordinatesLeft = offsetLeft.geometry.coordinates;
        const coordinatesRight = offsetRight.geometry.coordinates;

        // Create the bounding polygon by connecting the left and right offset lines
        const boundingCoords = [
            ...coordinatesLeft,
            ...coordinatesRight.reverse()  // Reverse to connect properly
        ];

        // Close the polygon by repeating the first point
        boundingCoords.push(boundingCoords[0]);

        return polygon([boundingCoords]);
    };

    const getItisDescription = (code: number): string => {
        try{
            const entry = Object.entries(ITISCodes).find(([, value]) => value === code);
            return entry ? entry[0] : `${code}`;
        } catch {
            return `${code}`;
        }

    };

    const decodeItisContent = (content: string): string => content.replace(/\b\d{3,}\b/g, (match) => getItisDescription(parseInt(match)));

    const createTimLayerPopupContent =(tim: TimData, geometryLocation: Location, pointLocation: Location) => (
            <div className={styles.popupContent}>
                <table>
                    <tbody>
                        <tr>
                            <td>Packet ID:</td>
                            <td>{tim.packetId}</td>
                        </tr>
                        <tr>
                            <td>Status:</td>
                            <td>{tim.status}</td>
                        </tr>
                        <tr>
                            <td>Type:</td>
                            <td>{geometryLocation.type}</td>
                        </tr>
                        {
                            pointLocation.points[0].relative?.bearingSlices?.length > 0 ?
                            <tr>
                                <td>Direction:</td>
                                <td>{pointLocation.points[0].relative?.bearingSlices.join(', ')}</td>
                            </tr>
                        : <></>
                        }
                        <tr>
                            <td>Content:</td>
                            <td>{decodeItisContent(tim.recordDetail.messagerecord.contentItems.map(x => x.content).join(''))}</td>
                        </tr>
                        <tr>
                            <td>Priority:</td>
                            <td>{tim.recordDetail.messagerecord.priority}</td>
                        </tr>
                        <tr>
                            <td>Start Time:</td>
                            <td>{tim.recordDetail.metadata.time.occurred}</td>
                        </tr>
                        <tr>
                            <td>Duration:</td>
                            <td>{formatDuration(tim.recordDetail.metadata.time.duration)}</td>
                        </tr>
                        <tr>
                            <td>End Time:</td>
                            <td>{calculateEndTime(tim.recordDetail.metadata.time.occurred, tim.recordDetail.metadata.time.duration)}</td>
                        </tr>
                        <tr>
                            <td>Time Ingested:</td>
                            <td>{tim.recordDetail.metadata.time.ingested}</td>
                        </tr>
                        <tr>
                            <td>UPER:</td>
                            <td>
                                <CodeBlockWithCopy code={tim.recordDetail.messagerecord.encodedMessage.encodedMessage} language={"UPER"} maxWidth={"25vw"}/>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
    );

    const createTimLayerRegion = (geometryLocation: Location, width: number, key :string, index: number, popupContent: JSX.Element, points: L.LatLngExpression[]) => {
        const style = {
                    color: 'blue', // Border color
                    weight: 1, // Line width
                    opacity: 1.0, // Line opacity
                    fillColor: 'blue', // Interior fill color
                    fillOpacity: 0.1 // Fill opacity for transparency
                };

        let geometry;
        try{
            switch (geometryLocation.type) {
                case 'polyline':
                    // Create bounding polygon around the polyline with specified width
                    const buffered = createBoundingPolygon(geometryLocation, width / 100000 / 2)

                    /// Add bounding poly
                    if(buffered?.geometry?.coordinates){
                        const coordinates = buffered.geometry.coordinates[0];
                        geometry = (
                            <Polygon key={`${key}-polyline-buffer-${index}`} positions={coordinates.map((coord: any) => [coord[1], coord[0]])}
                                pathOptions={style}
                                eventHandlers={{
                                    click: () => openDialog(popupContent, "J2735 TIM"),
                                }
                            }
                            />
                        );
                    }
                    break;
                case 'polygon':
                    geometry = (
                        <Polygon key={`${key}-polygon-${index}`} positions={points} pathOptions={style}
                            eventHandlers={{
                                click: () => openDialog(popupContent, "J2735 TIM"),
                            }}
                        />
                    );
                    break;
                case 'circle':
                    const defaultRadius = 0;
                    const radius = geometryLocation.points[0].absolute.radius ?? defaultRadius;
                    geometry = (
                        <Circle
                            key={`${key}-circle-${index}`}
                            center={points[0]}
                            radius={radius}
                            pathOptions={style}
                            eventHandlers={{
                                click: () => openDialog(popupContent, "J2735 TIM"),
                            }}
                        />
                    );
                    break;
                default:
                    geometry = null; // Cannot show TIM display region
            }
        } catch {
            geometry = null; // Cannot show TIM display region
        }

        return geometry;
    }

    const renderTimLayers = () => {
        if (!timData || timData.length === 0) return null;
        return timData.map((tim, index) => {
            try{
                if (tim.recordDetail.metadata.cancelled){
                    return null;
                }
                const locations = tim.recordDetail.metadata.locations;
                if (locations.length == 0) return null;
                const pointLocation = locations[0];
                const geometryLocation = locations.find(location => location.type !== 'point');
                const width = geometryLocation?.width || pointLocation?.width || 10;


                if (!pointLocation || !geometryLocation) return null;
                const points = geometryLocation.points.map((point: any) => [point.absolute.latitude, point.absolute.longitude] as LatLngExpression);
                const popupContent = createTimLayerPopupContent(tim, geometryLocation, pointLocation);
                const key = uuidv4();

                const marker = (
                    <Marker key={`${key}-point-${index}`} position={[pointLocation.points[0].absolute.latitude, pointLocation.points[0].absolute.longitude]} icon={timIcon}
                        eventHandlers={{
                            click: () => openDialog(popupContent, "J2735 TIM"),
                        }}
                    />
                );

                const geometry = createTimLayerRegion(geometryLocation, width, key, index, popupContent, points)

                return (
                    <React.Fragment key={`${uuidv4()}`}>
                        {marker}
                        {geometry}
                    </React.Fragment>
                );

            } catch (e) {
                console.error(`Failed to parse TIM: packet:${tim.packetId} id:${tim?.recordDetail?.metadata?.id}}`,e)
                return <React.Fragment />;
            }
        });
    };

    const checkShouldRender = () => {
        if (zoomLevel < MIN_ZOOM_LEVEL) return false;
        return true;
    };

    const openDialog = (content: JSX.Element, header:string) => {
        if(dialogVisible){
            setDialogVisible(false);
            setDialogContent(null);
            setDialogHeader("");
        }
        setDialogContent(content);
        setDialogVisible(true);
        setDialogHeader(header);
    };

    // Function to check if a given point is within the map bounds
    const isWithinMapBounds = (point: [number, number]): boolean => {
        if (!mapRef.current) {
            return false;
        }

        const latLngPoint = new LatLng(point[0], point[1]);

        // Get the map bounds
        const bounds: LatLngBounds = mapRef.current.getBounds();

        // Check if the point is within the bounds
        return bounds.contains(latLngPoint);
    };

    const getLaneFillOptions = (
        lane: LaneData,
        signalGroups: number[],
        signalStates: Map<number, any>,
        egressPath: boolean,
        MovementPhaseState: any
    ): { color: string, dashArray?: string, weight?: number } => {
        let fillOptions: { color: string, dashArray?: string, weight?: number } = { color: 'black', weight: LANE_WEIGHT };
        try{
            // If lane is Ingress then color it based on its current SPaT state.
            if (!egressPath) {
                // For each Signal Group get the current signal state array
                signalGroups.forEach(signalGroup => {
                    const signalState = signalStates.get(signalGroup);
                    if (signalState?.stateTimeSpeed) {
                        // If Signal State and Timing is defined then get the current state.
                        signalState.stateTimeSpeed.forEach((state: any) => {
                            switch (state.eventState) {
                                case MovementPhaseState.permissiveMovementAllowed:
                                    // Color the lane based on SPaT even State and make it dashed if left turn
                                    fillOptions = lane.maneuvers?.maneuverLeftAllowed && !lane.maneuvers?.maneuverStraightAllowed
                                        ? { color: signalStateToColor(state.eventState), dashArray: '5, 5', weight: LANE_WEIGHT }
                                        : { color: signalStateToColor(state.eventState), weight: LANE_WEIGHT };
                                    break;
                                case MovementPhaseState.stopThenProceed:
                                case MovementPhaseState.unavailable:
                                case MovementPhaseState.dark:
                                    // Color the lane based on SPaT even State and make it dashed
                                    fillOptions = { color: signalStateToColor(state.eventState), dashArray: '10, 10', weight: LANE_WEIGHT };
                                    break;
                                case MovementPhaseState.protectedMovementAllowed:
                                case MovementPhaseState.stopAndRemain:
                                case MovementPhaseState.preMovement:
                                case MovementPhaseState.permissiveClearance:
                                case MovementPhaseState.protectedClearance:
                                case MovementPhaseState.cautionConflictingTraffic:
                                default:
                                    // Color the lane based on SPaT even State
                                    fillOptions = { color: signalStateToColor(state.eventState), weight: LANE_WEIGHT };
                            }
                        });
                    }
                });
            } else {
                // Egress path, color orange
                fillOptions = { color: 'orange', weight: LANE_WEIGHT };
            }

            switch (lane.laneType) {
                case LaneTypeEnum.Crosswalk:
                    fillOptions = { color: 'gray', dashArray: '10, 10', weight: 3 };
                    break;
                case LaneTypeEnum.Sidewalk:
                    fillOptions = { color: 'gray', weight: 3 };
                    break;
                default:
                    break;
            }
        } catch (e) {
            console.error(e)
        }

        return fillOptions;
    };

    const renderMapMarker = () => (
        <React.Fragment key={`${uuidv4()}`}>
            {Array.from(combinedData.entries()).map(([intersectionId, data], index) => {
                try{
                    const { intersectionPoint, mapJ2735Uper, region, mapIngested } = data;
                    const position = intersectionPoint as LatLngTuple;

                    const popupContent =(
                        <div className={styles.popupContent}>
                            <table>
                                <tbody>
                                    <tr>
                                        <td>Intersection ID:</td>
                                        {region != null && region !== 0 ? `region: ${region} - ` : ''}id: {intersectionId}
                                    </tr>
                                    <tr>
                                        <td>Last Modified:</td>
                                        <td>{mapIngested ?? ''}</td>
                                    </tr>
                                    <tr>
                                        <td>UPER:</td>
                                        <td>
                                            <CodeBlockWithCopy code={mapJ2735Uper} language={"UPER"} maxWidth={"25vw"}/>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    );

                    return (
                        <React.Fragment key={uuidv4()}>
                            <Marker key={`marker-${uuidv4()}`} position={position}
                                    eventHandlers={{
                                        click: () => openDialog(popupContent, "J2735 MAP"),
                                    }}
                                    icon={mapIcon}
                                    />
                        </React.Fragment>
                    );
                } catch (e) {
                    return (<React.Fragment/>)
                }
            })}
        </React.Fragment>
    )

    const createPolyline = (laneType: LaneTypeEnum | null, positions: L.LatLngExpression[], fillOptions: any, popupContent: any) => (
        <Polyline
            key={uuidv4()}
            positions={positions}
            pathOptions={fillOptions}
            ref={(layer) => {
                if (layer && laneType === LaneTypeEnum.Vehicle) {
                    layer.arrowheads({ size: '3m', frequency: 'endonly' });
                }
                if (layer && laneType === LaneTypeEnum.Bike) {
                    layer.arrowheads({ size: '1m', frequency: 'endonly' });
                }
                return layer;
            }}
            eventHandlers={{
                click: () => openDialog(popupContent, "J2735 MAP"),
            }}
        />
    );

    const createLaneIdMarkerIcon = (laneIdIcon : L.DivIcon, positions: L.LatLngExpression[], popupContent: any) => (
        <Marker
            key={uuidv4()}
            position={positions[positions.length - 1]}
            icon={laneIdIcon}
            eventHandlers={{
                click: () => openDialog(popupContent, "J2735 MAP"),
            }}
        />
    );

    const getLaneIdIcon = (laneId: number) => divIcon({
        iconAnchor: [-10, 10],
        className: 'custom-div-icon',
        html: `<div style="background-color: transparent; padding: 2px; font-size: 10px;">${laneId}</div>`,
    });

    const getMapLanes = (lanes: LaneData[], signalStates : Map<number, SignalState>, region : number | null, mapJ2735Uper : string, mapIngested : string | null | undefined, intersectionId: number) => lanes.map((lane, laneIndex) => {
        const laneId = lane.laneId;
        const egressPath = lane.egressPath;
        const signalGroups = lane.signalGroups;

        let positions: LatLngExpression[] = lane.nodeList.nodes.map((node: MapNode) => [
            node.delta.nodeLatLon.latitude,
            node.delta.nodeLatLon.longitude
        ]);
        if (!egressPath) {
            positions = positions.reverse();
        }

        const fillOptions = getLaneFillOptions(lane, signalGroups, signalStates, egressPath, MovementPhaseState);

        const popupContent = (
            <div className={styles.popupContent}>
            <table>
                <tbody>
                    <tr>
                        <td>LaneId:</td>
                        <td>{laneId}</td>
                    </tr>
                    <tr>
                        <td>Signal Groups:</td>
                        <td>({signalGroups.join(", ")})</td>
                    </tr>
                    <tr>
                        <td>Intersection ID:</td>
                        {region != null && region !== 0 ? `region: ${region} - ` : ''}id: {intersectionId}
                    </tr>
                    <tr>
                        <td>Last Modified Time:</td>
                        <td>{mapIngested ?? ''}</td>
                    </tr>
                    <tr>
                        <td>UPER:</td>
                        <td>
                            <CodeBlockWithCopy code={mapJ2735Uper} language={"UPER"} maxWidth={"25vw"}/>
                        </td>
                    </tr>
                </tbody>
            </table>
            </div>
        );

        const laneIdIcon = getLaneIdIcon(laneId);
        const polylineElement = createPolyline(lane.laneType, positions, fillOptions, popupContent);
        const markerElement = createLaneIdMarkerIcon(laneIdIcon, positions, popupContent);

        return (
            <React.Fragment key={`${uuidv4()}-${laneIndex}`}>
                {polylineElement}
                {zoomLevel > 17 ? markerElement : null}
            </React.Fragment>
        );
    });

    const renderMapLayers = () => {
        if (combinedData.size === 0) return null;

        /// If map zoom is too low then only return Markers for the center of the intersection MAPs
        if (!checkShouldRender()){
            return renderMapMarker();
        }

        /// Loop through each intersection in the MAP + SPaT data and create lanes
        return Array.from(combinedData.entries()).map(([intersectionId, data], index) => {
            const { intersectionPoint, lanes, mapJ2735Uper, region, signalStates, mapIngested } = data;

            /// If intersection is not within current view bounds then do not render it.
            if (!isWithinMapBounds(intersectionPoint)) return null;
            try{
                return getMapLanes(lanes, signalStates, region, mapJ2735Uper, mapIngested, intersectionId);
            }
            catch (e) {
                console.error(`Failed to process MAP record for Intersection: ${intersectionId}`, e)
                return null;
            }
        });
    };

    function getAvailableManeuvers(maneuvers: Maneuver | null) {
        if (maneuvers == null){
            return [];
        }
        return Object.entries(maneuvers)
            .filter(([key, value]) => value)
            .map(([key, value]) => key);
        }

    function isNullOrEmpty(str: string | null | undefined): boolean {
        return str == null || (typeof str === 'string' && str.trim().length === 0);
    }

    const signalStateToColor = (eventState: MovementPhaseState) => {
        let color;
        switch (eventState) {
            case MovementPhaseState.permissiveMovementAllowed:
            case MovementPhaseState.protectedMovementAllowed:
                color = 'green';
                break;
            case MovementPhaseState.stopAndRemain:
            case MovementPhaseState.stopThenProceed:
                color = 'red';
                break;
            case MovementPhaseState.unavailable:
            case MovementPhaseState.dark:
                color = 'gray';
                break;
            case MovementPhaseState.preMovement:
            case MovementPhaseState.permissiveClearance:
            case MovementPhaseState.protectedClearance:
            case MovementPhaseState.cautionConflictingTraffic:
                color = 'yellow';
                break;
            default:
                color = 'blue';  // Default color
        }
        return color;
    }

    const determineLaneColor = (
        laneSignalGroups: number[],
        signalStates: Map<number, SignalState>
    ): { color: string; currentSignalStates: string[] } => {
        let color = 'gray';  // Default color
        const currentSignalStates: string[] = [];
        try{
            laneSignalGroups.forEach(signalGroup => {
                const signalState = signalStates.get(signalGroup);
                if (signalState?.stateTimeSpeed) {
                    signalState.stateTimeSpeed.forEach(state => {
                        currentSignalStates.push(state.eventState);
                        color = signalStateToColor(state.eventState as MovementPhaseState);
                    });
                }
            });
        } catch (e) {
            console.log(e)
        }

        return { color, currentSignalStates };
    };

    const getSPaTPopupContent = (intersectionId : number, signalStates : Map<number, SignalState>, lanes: LaneData[], spatJ2735Uper : string | undefined, spatIngested: string | null | undefined) => {
        if (isNullOrEmpty(spatJ2735Uper)) {
            return (
                <div className={styles.popupContent}>
                    <table>
                        <thead>
                            <tr>
                                <th/>
                                <th/>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Intersection ID:</td>
                                <td>{intersectionId}</td>
                            </tr>
                            <tr>
                                <td>Status:</td>
                                <td>No current SPaT data exists for this intersection</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            );
        }
        return (
            <div className={styles.popupContent}>
                <table>
                    <tbody>
                        <tr>
                            <td>Intersection ID:</td>
                            <td>{intersectionId}</td>
                        </tr>
                        <tr>
                            <td>Signal States:</td>
                            <td>
                                {Array.from(signalStates.entries()).map(([signalGroup, state], stateIndex) => {
                                    // Check if state is defined and has stateTimeSpeed property
                                    const stateValid = state && state.stateTimeSpeed && Array.isArray(state.stateTimeSpeed) && state.stateTimeSpeed.length > 0;

                                    // Ensure that stateTimeSpeed[0] and its eventState property exist
                                    const eventState = stateValid && state.stateTimeSpeed[0] && state.stateTimeSpeed[0].eventState
                                        ? state.stateTimeSpeed[0].eventState
                                        : 'Unknown State';

                                    // Filter and extract lane IDs safely
                                    const matchingLanes = Array.isArray(lanes)
                                        ? lanes.filter(lane => lane.signalGroups && Array.isArray(lane.signalGroups) && lane.signalGroups.includes(signalGroup))
                                        : [];

                                    const laneIds = matchingLanes.length > 0
                                        ? matchingLanes.map(lane => lane.laneId).join(', ')
                                        : 'No Lanes';

                                    return (
                                        <div key={uuidv4()}>
                                            Signal Group {signalGroup || 'Unknown Group'}: {eventState}
                                            <br />
                                            Lanes: {laneIds}
                                        </div>
                                    );
                                })}
                            </td>
                        </tr>
                        <tr>
                            <td>Ingested Time:</td>
                            <td>{spatIngested ?? 'No current SPaT data for this intersection'}</td>
                        </tr>
                        {spatJ2735Uper != null && (
                            <tr>
                                <td>UPER:</td>
                                <td>
                                    <CodeBlockWithCopy code={spatJ2735Uper} language={"UPER"} maxWidth={"25vw"} />
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        );
    };

    const getLaneEndMarkers = (lanes: LaneData[], signalStates : Map<number, SignalState>, intersectionId: number, spatIngested: string | null | undefined, spatJ2735Uper: string | null | undefined) => lanes.map((lane, laneIndex) => {
        const laneEndPosition = lane.endPoint;
        const laneSignalGroups = lane.signalGroups;
        if (laneSignalGroups.length === 0) {
            return null;
        }

        const availableManeuvers = getAvailableManeuvers(lane?.maneuvers ?? null);
        const { color, currentSignalStates } = determineLaneColor(laneSignalGroups, signalStates);

        const laneEndPopupContent = getLaneEndPopupContent(
            intersectionId,
            lane,
            laneSignalGroups,
            currentSignalStates,
            availableManeuvers,
            spatIngested,
            spatJ2735Uper
        );

        return (
            <CircleMarker
                key={`lane-marker-${uuidv4()}-${laneIndex}`}
                center={laneEndPosition}
                radius={getCircleMarkerRadius()}
                color={color}
                eventHandlers={{ click: () => openDialog(laneEndPopupContent, "J2735 SPaT") }}
            />
        );
    });

    const getLaneEndPopupContent = (intersectionId: number, lane: LaneData, laneSignalGroups: number[], currentSignalStates: string[], availableManeuvers: string[], spatIngested: string | null | undefined, spatJ2735Uper: string | null | undefined) => (
        <div className={styles.popupContent}>
            <table>
                <tbody>
                    <tr>
                        <td>Intersection ID:</td>
                        <td>{intersectionId}</td>
                    </tr>
                    <tr>
                        <td>LaneID:</td>
                        <td>{lane.laneId}</td>
                    </tr>
                    <tr>
                        <td>Signal Group:</td>
                        <td>{laneSignalGroups.join(', ')}</td>
                    </tr>
                    <tr>
                        <td>Current State:</td>
                        <td>{currentSignalStates.join(', ')}</td>
                    </tr>
                    <tr>
                        <td>Maneuvers:</td>
                        <td>{availableManeuvers.join(', ')}</td>
                    </tr>
                    <tr>
                        <td>Ingested Time:</td>
                        <td>{spatIngested ?? "No current SPaT data for this intersection"}</td>
                    </tr>
                    {spatJ2735Uper != null && (
                        <tr>
                            <td>UPER:</td>
                            <td>
                                <CodeBlockWithCopy code={spatJ2735Uper} language={"UPER"} maxWidth={"25vw"} />
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );

    const getCircleMarkerRadius = () => {
        if (zoomLevel > 20) return 15;
        if (zoomLevel > 18) return 10;
        return 5;
    };

    const renderSPaTLayers = () => {
        if (combinedData.size === 0) return null;

        if (!checkShouldRender()){
            return null;
        }

        const layers = Array.from(combinedData.entries()).map(([intersectionId, data], index) => {
            const { intersectionPoint, lanes, signalStates, spatJ2735Uper, spatIngested } = data;
            try{
                const popupContent = getSPaTPopupContent(intersectionId, signalStates, lanes, spatJ2735Uper, spatIngested);

                const laneEndMarkers = getLaneEndMarkers(lanes, signalStates, intersectionId, spatIngested, spatJ2735Uper);

                const intersectionIdIcon = divIcon({
                    iconAnchor: [20, 20],
                    iconSize: [40, 40],
                    className: styles.mui_intersection,
                    html: `<div class=${styles.text_center}>${intersectionId}</div>`
                });

                return (
                    <React.Fragment key={`intersection-${uuidv4()}-${index}`}>
                        {laneEndMarkers}
                        <Marker key={`marker-${uuidv4()}-${intersectionId}`}
                            position={intersectionPoint}
                            icon={intersectionIdIcon}
                            eventHandlers={{
                                    click: () => openDialog(popupContent, "J2735 SPaT"),
                            }}
                        />
                    </React.Fragment>
                );
            } catch (e) {
                console.error(`Failed to render SPaT for intersection: ${intersectionId}`, e)
                return (< React.Fragment />)
            }
        });

        return layers;
    };

    const handleMapZoom = () => {
        if (mapRef.current) {
            setZoomLevel(mapRef.current.getZoom());
        }
    };

    useEffect(() => {
      handleMapPan();
    }, [zoomLevel])

    const handleMapPan = () => {
        if (mapRef.current) {
            const _intersectionIds: number[]= [];
            if(checkShouldRender()){
                if (combinedData.size === 0) return null;
                /// Loop through each intersection in the MAP + SPaT data and create lanes
                combinedData.forEach((data,intersectionId) => {
                    const { intersectionPoint } = data;
                    /// If intersection is not within current view bounds then do not render it.
                    if (isWithinMapBounds(intersectionPoint)){
                        _intersectionIds.push(intersectionId);
                    }
                });
            }
            setIntersectionIds(_intersectionIds);
        }
    };

    const MapEventsHandler = () => {
        useMapEvents({
            zoomend: () => handleMapZoom(),
            contextmenu: (e) => {
            const { lat, lng } = e.latlng;
            setPopupInfo({
                position: e.latlng,
                content: `Latitude: ${lat.toFixed(6)}, Longitude: ${lng.toFixed(6)}`
            });
            },
            moveend: () => {
                handleMapPan();
            },
        });

        return null;
    };


    return (
        <div data-testid="CvdfVisualizer">
            <Toast ref={toast} />
            <MapContainer
                center={latLng(configData.mapCenter.lat, configData.mapCenter.lon)}
                zoom={INITIAL_ZOOM_LEVEL}
                className={styles.MapContainer}
                style={{ height: '95%', width: '100%' }}
                ref={mapRef}
                maxZoom={24}
                >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' />
                <MapEventsHandler />
                <ScaleControl
                    aria-label="Map scale control"
                    position="bottomleft"
                />

                 <LayersControl
                        aria-label="Map layer control"
                        position="topleft"
                    >
                        <LayersControl.Overlay
                            aria-label="Traveler Information Message Layer"
                            checked
                            name={"TIM"}
                        >
                            <LayerGroup ref={setTIMLayer}>
                                {renderTimLayers()}
                            </LayerGroup>
                        </LayersControl.Overlay>

                        <LayersControl.Overlay
                            aria-label="Intersection MAP Layer"
                            checked
                            name={"MAP"}
                        >
                            <LayerGroup ref={setMAPLayer}>
                                {renderMapLayers()}
                            </LayerGroup>
                        </LayersControl.Overlay>

                        <LayersControl.Overlay
                            aria-label="Signal Phase and Timing Layer"
                            checked
                            name={"SPaT"}
                        >
                            <LayerGroup ref={setSPaTLayer}>
                                {renderSPaTLayers()}
                            </LayerGroup>
                        </LayersControl.Overlay>
                    </LayersControl>
                    {popupInfo ? (
                        <Popup position={popupInfo.position}>
                            <div>{popupInfo.content}</div>
                        </Popup>
                    ) : null}
            </MapContainer>

            <Dialog onHide={() => setDialogVisible(false)} position="bottom-left" visible={dialogVisible} modal={false}
                header={dialogHeader}
                className={styles.popupDialog}
                contentClassName={styles.popupDialog}
                headerClassName={styles.popupDialog}
            >
                {dialogContent}
            </Dialog>

            <div>
                <p>* MAP and SPaT data detail visible at higher zoom levels.<br/>
                * MAP data is refreshed on a 1 hour interval.<br/>
                * TIM data is refreshed on a 1 minute interval.
                </p>
            </div>

        </div>
    );

};

interface CodeBlockWithCopyProps {
  code: string;
  language: string;
  maxWidth: string;
}

const CodeBlockWithCopy: FC<CodeBlockWithCopyProps> = ({ code, language, maxWidth }) => {
  const [copied, setCopied] = _useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // Reset "copied" state after 2 seconds
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  return (
    <div style={{ position: 'relative', backgroundColor: '#282c34', color: '#fff', padding: '5px', borderRadius: '8px'}}>
      <div style={{marginBottom: '5px', marginTop: '30px'}}>
        <pre style={{ margin: 0, overflowX: 'auto', maxWidth: maxWidth}}>
          <code className={`language-${language}`}>
            {code}
          </code>
        </pre>
      </div>
       <div style={{ position: 'absolute', top: '2px', right: '10px' }}>
        <button
          onClick={handleCopy}
          style={{
            backgroundColor: copied ? '#4CAF50' : '#007bff',
            color: 'white',
            border: 'none',
            padding: '8px 12px',
            borderRadius: '5px',
            cursor: 'pointer',
            transition: 'background-color 0.3s ease',
          }}
        >
          <i className="pi pi-copy"/>
        </button>
      </div>
    </div>
  );
};

export default CvdfVisualizer;
